import { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Breadcrumbs,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
} from "@material-ui/core";

import ImageCard from "./ImageCard";
import ImageCardGroup from "./ImageCardGroup";
import FolderCard from "./FolderCard";
import BackDirectoryCard from "./BackDirectoryCard";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { closeModal } from "../../store/modal/actions";

const FolderView = (props: FolderViewProps) => {
  const [opencopyURLmessage, setOpenCopyURLmessage] = useState(false);
  const state = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const classes = useStyles();

  // Group images by group_id as an array
  const groupedImagesArray = useMemo(() => {
    const groups = [] as { groupId: string | null; items: AssetItem[] }[];

    const groupedById = new Map<string, AssetItem[]>();
    const ungrouped: AssetItem[] = [];

    // Iterate over children and group them
    props.currentDirectory.children.forEach((image: any) => {
      if (image.type === "FILE") {
        const groupId = image.asset?.metadata?.group_id || null;

        if (groupId) {
          // Group by group_id
          if (!groupedById.has(groupId)) {
            groupedById.set(groupId, []);
          }
          groupedById.get(groupId)?.push(image);
        } else {
          // Ungrouped images
          ungrouped.push(image);
        }
      }
    });

    // Convert the map into an array of groups
    groupedById.forEach((items, groupId) => {
      groups.push({ groupId, items });
    });

    // Add ungrouped images as a separate group
    if (ungrouped.length > 0) {
      groups.push({ groupId: null, items: ungrouped });
    }

    return groups;
  }, [props.currentDirectory.children]);

  const handleCopyToClipboard = (url: string) => {
    if (!props.isBannerImageSelectModal) {
      //this function has to do nothing if it is a child of the BannerImageSelectorModal
      setOpenCopyURLmessage(true);
      setTimeout(() => {
        if (state.modal_reducer.isOpen) {
          dispatch(closeModal());
        }
        setOpenCopyURLmessage(false); // Hide the message after 3 seconds since there is no user interaction with this message
      }, 1000);
      navigator.clipboard.writeText(url);
    }
  };

  const handleDeleteAsset = (filePath: string) => {
    const updatedAssets = props.assets.filter(
      (asset) => asset.physical_file_path !== filePath
    );
    props.updateAssets(updatedAssets);
  };

  const handleDeleteGroupAssets = (filePaths: string[]) => {
    const updatedAssets = props.assets.filter(
      (asset) => !filePaths.includes(asset.physical_file_path)
    );
    props.updateAssets(updatedAssets);
  };

  const children = useMemo(() => {
    const sortedChildren = props.currentDirectory.children.sort(
      (a: any, b: any) => {
        if (a.type < b.type) {
          return -1;
        }
        if (a.type > b.type) {
          return 1;
        }
        return 0;
      }
    );
    return sortedChildren;
  }, [props.currentDirectory, props.assets]);

  return (
    <>
      {/* this is the back directory component (breadcrumbs) */}
      {props.parentDirectories.length > 0 ? (
        <div className={classes.navigationControls}>
          <BackDirectoryCard handleBack={props.handleBack} />
          <Breadcrumbs>
            {props.parentDirectories.map((directory: any, index) => (
              <Typography color="textPrimary" key={index}>
                {directory.name}
              </Typography>
            ))}
            <Typography color="textPrimary">
              {props.currentDirectory.name}
            </Typography>
          </Breadcrumbs>
        </div>
      ) : null}

      <Grid container spacing={2}>
        {/* Render directories */}
        {children.map(
          (child: any) =>
            child.type === "DIRECTORY" && (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={`${child.path}/${child.name}`}
              >
                <FolderCard
                  directory={child}
                  handleDirectorySelection={props.handleDirectorySelection}
                />
              </Grid>
            )
        )}
        {/* if not a directory, then render files from groupedImagesArray */}
        {/* Loop through groupedImagesArray and render grouped images and folders without groupID*/}
        {groupedImagesArray.map(
          (group) =>
            !group.groupId &&
            group.items.map((item: any) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={`${item.path}/${item.name}`}
              >
                {item.type === "FILE" && (
                  <ImageCard
                    imageUrl={item.asset.url}
                    imageFilePath={item.asset.physical_file_path}
                    imageMeta={item.asset.metadata}
                    imageName={item.asset.name}
                    setSelectedImage={props.setSelectedImage}
                    selectedImageUrl={props.selectedImageUrl}
                    handleDeleteAsset={handleDeleteAsset}
                    handleCopyToClipboard={handleCopyToClipboard}
                  />
                )}
              </Grid>
            ))
        )}

        {/* Loop through groupedImagesArray and render grouped images (not folders) that has the same groupId.*/}
        {groupedImagesArray.map(
          (group) =>
            group.groupId &&
            group.items.map(
              (item: any) =>
                item.type === "FILE" &&
                item.name.includes("large") && (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={`${item.path}/${item.name}`}
                  >
                    <ImageCardGroup
                      imageUrl={item.asset.url}
                      imageFilePath={item.asset.physical_file_path}
                      imageName={item.asset.name}
                      setSelectedImage={props.setSelectedImage}
                      selectedImageUrl={props.selectedImageUrl}
                      handleDeleteGroupAssets={handleDeleteGroupAssets}
                      handleCopyToClipboard={handleCopyToClipboard}
                      group={group.items}
                      isBannerImageSelectModal={props.isBannerImageSelectModal}
                    />
                  </Grid>
                )
            )
        )}
      </Grid>
      {/* copy url confirmation message */}
      <Dialog open={opencopyURLmessage}>
        <DialogTitle>{"File URL Copied!"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The URL has been copied to the clipboard
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface FolderViewProps {
  currentDirectory: any;
  parentDirectories: any[];
  handleDirectorySelection: any;
  handleBack: any;
  assets: any[];
  setSelectedImage?: (url: string) => void;
  selectedImageUrl?: string;
  updateAssets: (updatedAssets: any[]) => void;
  isBannerImageSelectModal?: boolean;
}

interface AssetItem {
  created_at: string;
  id: string;
  metadata: Metadata;
  name: string;
  physical_file_path: string;
  updated_at: string;
  url: string;
}
interface Metadata {
  group_id?: string; // Assuming group_id is optional
}

const useStyles = makeStyles((theme) => ({
  navigationControls: {
    display: "flex",
    alignItems: "center",
    padding: "20px 0px",
  },
}));

export default FolderView;
